import {useEffect, useId, useRef, useState} from 'react';
import {QRCodeSVG} from 'qrcode.react';
import {ErrorNotice, Loading, dateTime} from './components';
import {useHermes} from './lib/store';
import type {HermesState} from './lib/types';
import {APP_BASE, Link, useSearch} from './router';
import {messageDisplayPrefix, userMessageDisplayText} from './lib/conversation-messages';

type Actions = ReturnType<typeof useHermes>['actions'];

export function WhatsAppSettings({state, actions, surface = 'settings', sessionId}: {
  state: HermesState; actions: Actions; surface?: 'settings' | 'workspace'; sessionId?: string;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const autoPairStarted = useRef(false);
  const [connectionRead, setConnectionRead] = useState(false);
  const search = useSearch();
  const resource = state.whatsapp;
  const status = resource.data;
  const connection = status?.connection;
  const linkedChat = status?.chat;
  const prefix = messageDisplayPrefix(state);
  const linkedTitle = linkedChat?.title && prefix ? userMessageDisplayText(linkedChat.title, prefix, {folded: true}) : null;
  const canChangeChat = Boolean(sessionId && linkedChat?.sessionId !== sessionId);
  const accountId = state.session.data?.accountId;
  const busy = state.operation.label !== null;
  const pairing = connection?.status === 'starting' || connection?.status === 'pairing';

  useEffect(() => {
    if (surface !== 'settings' || !accountId || new URLSearchParams(search).get('connect') !== 'whatsapp') return;
    heading.current?.scrollIntoView({block: 'start'});
    heading.current?.focus({preventScroll: true});
  }, [accountId, search, surface]);

  useEffect(() => {
    if (!accountId) return;
    let stopped = false;
    const refresh = (): void => {
      if (document.visibilityState === 'visible') void actions.refreshWhatsApp().then(() => {
        if (!stopped) setConnectionRead(true);
      }).catch(() => undefined);
    };
    refresh();
    document.addEventListener('visibilitychange', refresh);
    return () => {stopped = true; document.removeEventListener('visibilitychange', refresh);};
  }, [accountId, actions]);

  useEffect(() => {
    if (surface !== 'workspace' || !sessionId || !accountId || !connectionRead || autoPairStarted.current || busy ||
        resource.status !== 'ready' || !status || !state.accountSettings.data?.modelKey.configured) return;
    if (status.runtime !== 'stopped' && connection?.status !== 'disconnected') return;
    autoPairStarted.current = true;
    void actions.pairWhatsApp(sessionId).catch(() => undefined);
  }, [surface, sessionId, accountId, connectionRead, busy, resource.status, status, connection?.status, state.accountSettings.data?.modelKey.configured, actions]);

  useEffect(() => {
    if (!accountId || !pairing || busy) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async (): Promise<void> => {
      if (document.visibilityState === 'visible') {
        try {await actions.refreshWhatsApp();}
        catch { /* The WhatsApp resource displays the full request error. */ }
      }
      if (!stopped) timer = setTimeout(() => void poll(), 2000);
    };
    timer = setTimeout(() => void poll(), 2000);
    return () => {stopped = true; clearTimeout(timer);};
  }, [accountId, pairing, busy, actions]);

  return <section className={`settings-section settings-whatsapp${surface === 'workspace' ? ' workspace-whatsapp' : ''}`} aria-labelledby={titleId}>
    <div className="settings-description"><h2 id={titleId} ref={heading} tabIndex={-1}>
      <img src={`${APP_BASE}/assets/whatsapp-official.svg`} width={23} height={23} alt=""/>WhatsApp</h2>
      <p>Link your WhatsApp account. Talk to your assistant in Message yourself.</p>
      <p>Continue the same conversation from the website. Your saved preferences stay available.</p>
      <p>Open WhatsApp on your phone. Open Linked devices, select Link a device, then scan the code.</p>
      {connection?.status === 'connected' && <>
        <p className="settings-key-status" role="status">Your WhatsApp account is linked.</p>
        <p className="settings-whatsapp-identity">{connection.name && <span>{connection.name}</span>}
          {connection.phone && <span>{connection.phone}</span>}</p>
        {linkedChat && <p>Linked chat: <Link href={`/workspace?chat=${encodeURIComponent(linkedChat.sessionId)}`}>
          {linkedTitle || 'Untitled chat'}</Link></p>}
      </>}
    </div>
    <div className="whatsapp-controls">
      <ErrorNotice error={resource.error}/>
      {!status && resource.status !== 'error' && <Loading/>}
      {status && <>
        {status.runtime === 'stopped' && <p className="field-help">Your team is stopped. Connect WhatsApp to start your team and check the connection.</p>}
        {connection?.status === 'disconnected' && <p className="whatsapp-connection-status" role="status">WhatsApp is not connected.</p>}
        {pairing && <div className="whatsapp-qr">
          <div className="whatsapp-qr-slot">{connection?.qr ?
            <QRCodeSVG value={connection.qr} size={256} level="M" marginSize={4}
              bgColor="#ffffff" fgColor="#000000" title="Scan with WhatsApp to link your phone"/> : <Loading/>}</div>
          <p className="field-help whatsapp-qr-expiry">{connection?.expiresAt && <>Pairing ends {dateTime(connection.expiresAt)}.</>}</p>
        </div>}
        {connection?.error && <div className="error-notice" role="alert"><p>WhatsApp connection error</p><pre>{connection.error}</pre></div>}
        <div className="button-row">
          {connection?.status !== 'connected' && <button type="button" className="button"
            disabled={busy || resource.status === 'loading' || pairing}
            onClick={() => void actions.pairWhatsApp(sessionId).catch(() => undefined)}>Connect WhatsApp</button>}
          {connection?.status === 'connected' && canChangeChat && <button type="button" className="button"
            disabled={busy || resource.status === 'loading'} onClick={() => void actions.pairWhatsApp(sessionId).catch(() => undefined)}>Use this chat</button>}
          {connection && connection.status !== 'disconnected' && <button type="button" className="text-button" disabled={busy}
            onClick={() => void actions.disconnectWhatsApp().catch(() => undefined)}>{pairing ? 'Cancel pairing' : 'Disconnect WhatsApp'}</button>}
        </div>
        {connection?.status === 'connected' && <p className="field-help">Disconnecting removes this WhatsApp link. Your conversation stays in your account.</p>}
      </>}
    </div>
  </section>;
}
